import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { msUntilSessionExpiry } from '../utils/session-age';

/**
 * Hace cumplir el tope de 12 h en la pestaña que no genera peticiones.
 *
 * El chequeo de `CognitoService.getSession()` cubre todo lo que consulta la
 * sesión — interceptor, guards, socket —, pero una pestaña abierta y quieta no
 * consulta nada y se quedaría dentro para siempre. Acá se agenda el corte y,
 * sobre todo, se revalida al volver al foreground: en móvil el navegador
 * congela los timers al pasar a background, así que al volver hay que medir
 * contra el reloj y no confiar en que el `setTimeout` disparó.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService implements OnDestroy {
  private timerId: any = null;
  private started = false;
  private subscription = new Subscription();

  constructor(
    private cognito: CognitoService,
    private zone: NgZone,
  ) {}

  /** Idempotente: se puede llamar en cada arranque sin duplicar listeners. */
  start(): void {
    if (this.started) return;
    this.started = true;

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Al iniciar sesión hay que agendar el corte de la sesión nueva; al
    // cerrarla, desagendar el de la vieja.
    this.subscription.add(
      this.cognito.authState$.subscribe(() => {
        void this.check();
      }),
    );

    // Cubre la recarga de página, donde no hay evento de login que escuchar.
    void this.check();
  }

  stop(): void {
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    this.started = false;
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.check();
    }
  };

  /**
   * Mide contra el reloj y reagenda. `getSession()` ya corta por su cuenta si
   * la sesión venció, así que acá alcanza con pedirla.
   */
  private async check(): Promise<void> {
    this.clearTimer();

    const session = await this.cognito.getSession();
    if (!session) return;

    const remaining = msUntilSessionExpiry(session);
    if (remaining === null) return;

    // +1 s de margen para no despertar justo en el borde y encontrar que
    // todavía falta un milisegundo.
    this.scheduleIn(remaining + 1000);
  }

  private scheduleIn(ms: number): void {
    this.zone.runOutsideAngular(() => {
      this.timerId = setTimeout(() => {
        this.zone.run(() => void this.check());
      }, ms);
    });
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
